import {
  buildOccContract,
  getDteFromContractExpiry,
  getDteFromManualOcc,
} from "./watchOptionContracts";
import type {
  AlertMode,
  ContractMode,
  ContractType,
  RecipeCondition,
  TargetDirection,
  VolatilitySensitivity,
} from "./watchTypes";

export interface WatchPayloadState {
  ticker: string;
  intent: string;
  intentRequiresContract: boolean;
  contractMode: ContractMode;
  contractType: ContractType;
  contractExpiry: string;
  contractStrike: string;
  manualOCC: string;
  duration: number;
  interval: number;
  maxAlerts: number;
  cooldown: number;
  marketHoursOnly: boolean;
  confidenceThreshold: number;
  ivThreshold: number;
  targetPrice: number | null;
  targetDirection: TargetDirection;
  volatilitySensitivity: VolatilitySensitivity;
  alertMode: AlertMode;
  conditions: unknown[];
  conditionLogic: string;
  recipeConditions: RecipeCondition[];
  position?: Record<string, unknown>;
  positionProfitTarget?: number | null;
  positionLossLimit?: number | null;
  /** Legacy single-threshold fields retained for older embedded callers. */
  positionPnlThreshold?: number | null;
  positionPnlDirection?: "profit" | "loss";
}

function capDurationWithDtes(duration: number, dtes: number[]) {
  const validDtes = dtes.filter(Number.isFinite);
  if (validDtes.length === 0) return duration;
  const earliestDte = Math.min(...validDtes);
  if (earliestDte < 0) throw new Error("Option contract has expired");
  return Math.max(1, Math.min(duration, earliestDte));
}

/** Return the exact monitoring duration for an option expiry-bound Watch. */
export function capWatchDurationAtExpiry(duration: number, expiries: string[]) {
  return capDurationWithDtes(
    duration,
    expiries
      .map((expiry) => getDteFromContractExpiry(expiry))
      .filter((dte): dte is number => dte !== null),
  );
}

export function buildWatchRequestBody(state: WatchPayloadState) {
  let finalDuration = state.duration;
  let finalOCC = "";

  if (state.intentRequiresContract && !state.position) {
    if (state.contractMode === "builder") {
      if (!state.contractExpiry) {
        throw new Error("Expiry date is required");
      }

      finalDuration = capWatchDurationAtExpiry(finalDuration, [state.contractExpiry]);

      finalOCC = buildOccContract({
        ticker: state.ticker,
        contractExpiry: state.contractExpiry,
        contractStrike: state.contractStrike,
        contractType: state.contractType,
      });
    } else {
      finalOCC = state.manualOCC.trim().toUpperCase();
      if (!finalOCC) {
        throw new Error("OCC is required");
      }

      const diffDays = getDteFromManualOcc(finalOCC);
      finalDuration = capDurationWithDtes(
        finalDuration,
        diffDays === null ? [] : [diffDays],
      );
    }
  }

  if (state.position) {
    const basis = state.position.position_basis as { legs?: Array<{ option_contract?: string }> } | undefined;
    const legDtes = (basis?.legs || [])
      .map((leg) => getDteFromManualOcc(leg.option_contract || ""))
      .filter((dte): dte is number => dte !== null);
    try {
      finalDuration = capDurationWithDtes(finalDuration, legDtes);
    } catch {
      throw new Error("A position leg has expired");
    }
  }

  let intentParamsPayload: Record<string, unknown> | undefined;
  if (!state.position && (state.intent === "good_time_to_buy" || state.intent === "good_time_to_sell")) {
    intentParamsPayload = { confidence_threshold: state.confidenceThreshold };
  } else if (!state.position && state.intent === "iv_rank_threshold") {
    intentParamsPayload = { threshold: state.ivThreshold };
  } else if (!state.position && state.intent === "price_target") {
    if (!state.targetPrice) throw new Error("Target price is required");
    intentParamsPayload = {
      target_price: state.targetPrice,
      direction: state.targetDirection,
    };
  } else if (!state.position && state.intent === "volatility_spike") {
    intentParamsPayload = { sensitivity: state.volatilitySensitivity };
  }

  const policyMaxAlerts = state.alertMode === "one_and_done" ? 1 : 20;
  const policyCooldown = state.alertMode === "one_and_done" ? 0 : state.cooldown;

  const body: Record<string, unknown> = {
    ticker: state.ticker.toUpperCase(),
    intent: state.intent,
    policy: {
      max_duration_days: finalDuration,
      evaluation_interval_minutes: state.interval,
      max_alerts_per_watch: policyMaxAlerts,
      cooldown_minutes: policyCooldown,
      market_hours_only: state.marketHoursOnly,
    },
  };
  if (intentParamsPayload) body.intent_params = intentParamsPayload;
  if (state.position) body.position = state.position;
  if (finalOCC) body.option_contract = finalOCC;
  // Position watches intentionally accept only deterministic position-P&L
  // conditions. Do not silently send unrelated single-contract conditions
  // that the valuation packet cannot evidence.
  const conditions = state.position ? [] : [...state.conditions];
  if (state.position) {
    const hasNewThresholds = state.positionProfitTarget !== undefined
      || state.positionLossLimit !== undefined;
    const profitTarget = hasNewThresholds
      ? state.positionProfitTarget
      : state.positionPnlDirection === "loss" ? null : state.positionPnlThreshold;
    const lossLimit = hasNewThresholds
      ? state.positionLossLimit
      : state.positionPnlDirection === "loss" ? state.positionPnlThreshold : null;
    const thresholdValues = [profitTarget, lossLimit].filter(
      (threshold): threshold is number => threshold !== null && threshold !== undefined,
    );
    if (thresholdValues.length === 0) {
      throw new Error("Enter a profit target, a loss limit, or both");
    }
    if (thresholdValues.some((threshold) => !Number.isFinite(threshold) || threshold <= 0)) {
      throw new Error("Position P&L alert amounts must be greater than zero");
    }
    if (profitTarget !== null && profitTarget !== undefined) {
      conditions.push({
        dimension: "position_unrealized_pnl_usd",
        operator: "gte",
        threshold: Math.abs(profitTarget),
      });
    }
    if (lossLimit !== null && lossLimit !== undefined) {
      conditions.push({
        dimension: "position_unrealized_pnl_usd",
        operator: "lte",
        threshold: -Math.abs(lossLimit),
      });
    }
  }
  if (conditions.length > 0) {
    body.conditions = conditions;
    body.condition_logic = state.conditionLogic;
  }
  if (!state.position && state.recipeConditions.length > 0) {
    body.recipe_conditions = state.recipeConditions.map((item) => ({
      id: item.id,
      enabled: item.enabled,
    }));
  }
  return body;
}

export function buildUpdateWatchBody(
  state: Pick<
    WatchPayloadState,
    | "duration"
    | "interval"
    | "maxAlerts"
    | "cooldown"
    | "marketHoursOnly"
    | "alertMode"
    | "confidenceThreshold"
    | "ivThreshold"
    | "targetPrice"
    | "targetDirection"
    | "volatilitySensitivity"
    | "conditions"
    | "conditionLogic"
    | "recipeConditions"
  > & { watchIntent: string },
) {
  const effectiveMaxAlerts = state.alertMode === "one_and_done" ? 1 : state.maxAlerts;
  const effectiveCooldown = state.alertMode === "one_and_done" ? 0 : state.cooldown;
  const body: Record<string, unknown> = {
    policy: {
      max_duration_days: state.duration,
      evaluation_interval_minutes: state.interval,
      max_alerts_per_watch: effectiveMaxAlerts,
      cooldown_minutes: effectiveCooldown,
      market_hours_only: state.marketHoursOnly,
    },
  };

  if (state.watchIntent === "good_time_to_buy" || state.watchIntent === "good_time_to_sell") {
    body.intent_params = { confidence_threshold: state.confidenceThreshold };
  } else if (state.watchIntent === "iv_rank_threshold") {
    body.intent_params = { threshold: state.ivThreshold };
  } else if (state.watchIntent === "price_target") {
    body.intent_params = {
      target_price: state.targetPrice,
      direction: state.targetDirection,
    };
  } else if (state.watchIntent === "volatility_spike") {
    body.intent_params = { sensitivity: state.volatilitySensitivity };
  }

  if (state.conditions.length > 0) {
    body.conditions = state.conditions;
    body.condition_logic = state.conditionLogic;
  }
  if (state.recipeConditions.length > 0) {
    body.recipe_conditions = state.recipeConditions.map((item) => ({
      id: item.id,
      enabled: item.enabled,
    }));
  }
  return body;
}
